import { useState, useEffect, useRef } from 'react';
import { CHAT_CONFIG } from '../../config/chatConfig';

const useUserActivity = () => {
  const [isActive, setIsActive] = useState(true);
  const [isDormant, setIsDormant] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);

  // Define updateActivity outside useEffect so it can be referenced by triggerActivity
  const updateActivity = () => {
    lastActivityRef.current = Date.now();
    if (!isActive) {
      setIsActive(true);
    }
    if (isDormant) {
      setIsDormant(false);
    }
  };

  useEffect(() => {

    // Activity event listeners
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Online/offline detection
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check activity periodically
    checkIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      
      const { inactivityThreshold, dormantThreshold } = CHAT_CONFIG.polling.activity;
      
      // Check for dormant state (15+ minutes)
      if (timeSinceActivity > dormantThreshold && !isDormant) {
        setIsDormant(true);
        setIsActive(false);
      }
      // Check for inactive state (5+ minutes)
      else if (timeSinceActivity > inactivityThreshold && isActive && !isDormant) {
        setIsActive(false);
      }
    }, 30000); // Check every 30 seconds

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isActive, isDormant]);

  // Get current activity state
  const getActivityState = () => {
    if (!isOnline) return 'offline';
    if (isDormant) return 'dormant';
    if (!isActive) return 'inactive';
    return 'active';
  };

  // Get appropriate polling interval based on activity state
  const getPollingInterval = () => {
    const intervals = CHAT_CONFIG.polling.intervals;
    
    switch (getActivityState()) {
      case 'offline':
        return intervals.dormant; // Use dormant interval when offline
      case 'dormant':
        return intervals.dormant;
      case 'inactive':
        return intervals.background;
      case 'active':
      default:
        return intervals.normal;
    }
  };

  // Force activity state (useful for triggering fast polling after actions)
  const triggerActivity = updateActivity;

  return {
    isActive,
    isDormant,
    isOnline,
    activityState: getActivityState(),
    pollingInterval: getPollingInterval(),
    triggerActivity,
    
    // Convenience flags
    shouldPoll: isOnline && (isActive || !isDormant),
    shouldFastPoll: isActive && isOnline
  };
};

export default useUserActivity;